/* eslint-disable no-template-curly-in-string */
import styled, { css, keyframes } from 'styled-components';
import { Typography } from '../../global/theme';
import { mobile_xl } from '../../global/mediaQueryMixin';
import { BUTTON_TYPE } from '../../global/constants';
import { FlexCenter } from '../../global/commonStyle';

interface WrapButton {
  isLoading?: boolean;
  variant?: number;
  size?: 'small' | 'medium' | 'large';
  iconPosition?: 'start' | 'end' | 'none';
  disabled?: boolean;
  width?: string;
}

const getBackground = (props: WrapButton) => {
  if (props.variant === BUTTON_TYPE.CRITICAL) return 'var(--accent-error)';
  if (props.variant === BUTTON_TYPE.SECONDARY) return 'var(--background-primary)';
  if (props.isLoading) return 'var(--brand-primary-hover)';
  if (props.variant === BUTTON_TYPE.TERTIARY) return 'transparent';
  return 'var(--neutral-secondary)';
};

const Buttons = styled.button<WrapButton>`
  width: ${({ width }) => width};
  ${FlexCenter}
  gap: var(--space-4);
  ${Typography.body_md_medium}
  font-family: unset;
  padding: var(--space-6) var(--space-12);
  border-radius: var(--border-radius-8);
  background-color: var(--brand-primary);
  border: unset;
  color: var(--text-white);
  white-space: nowrap;
  cursor: pointer;
  &:hover {
    background-color: var(--brand-primary-hover);
  }
  /* default / primary button */
  ${({ size }) =>
    size === 'small' &&
    css`
      padding: var(--space-5) var(--space-10);
      ${Typography.body_sm_medium}
      border-radius: var(--border-radius-8);
      svg {
        width: 14px;
        height: 14px;
      }
    `}
  ${({ size }) =>
    size === 'large' &&
    css`
      ${Typography.body_lg_medium}
      padding: var(--space-11) var(--space-16);
      svg {
        width: 20px;
        height: 20px;
      }
    `}
  
  /* icon Position */
    ${({ iconPosition, size }) =>
    iconPosition === 'end' &&
    css`
      flex-direction: row-reverse;
      padding-inline: ${size === 'small'
        ? 'var(--space-10) var(--space-8)'
        : size === 'large'
        ? 'var(--space-16) var(--space-12)'
        : 'var(--space-12) var(--space-10)'};
    `}
      ${({ iconPosition, size }) =>
    iconPosition === 'start' &&
    css`
      flex-direction: row;
      padding-inline: ${size === 'small'
        ? 'var(--space-8) var(--space-10)'
        : size === 'large'
        ? 'var(--space-12) var(--space-16)'
        : 'var(--space-10)var(--space-12)'};
    `}
  
    /* Secondary button */
  ${({ variant }) =>
    variant === BUTTON_TYPE.SECONDARY &&
    css`
      background-color: var(--background-primary);
      color: var(--text-primary);
      box-shadow: var(--border);

      &:hover {
        background-color: var(--background-secondary);
        box-shadow: var(--button-border-hover);
      }
    `}


      
    /* Tertiary button */
  ${({ variant }) =>
    variant === BUTTON_TYPE.TERTIARY &&
    css`
      background-color: transparent;
      color: var(--text-primary);
      border-radius: var(--border-radius-8);

      &:hover {
        background-color: var(--background-secondary);
      }
    `}


    /* Critical */
    ${({ variant }) =>
    variant === BUTTON_TYPE.CRITICAL &&
    css`
      background: var(--accent-error);
      color: var(--text-white);
      &:hover {
        background: var(--accent-error);
        color: var(--text-white);
      }
    `}

    /* disabled button */
  ${(props) =>
    props.disabled &&
    css`
      background: ${getBackground(props)};
      color: var(--text-tertiary);
      cursor: not-allowed;
      &:hover {
        background: ${getBackground(props)};
        box-shadow: ${props.variant === BUTTON_TYPE.SECONDARY ? 'var(--border)' : 'none'};
      }
    `}
    
  ${mobile_xl(css<WrapButton>`
    white-space: nowrap;
  `)}
`;

const ButtonWrap = styled.div<WrapButton>`
  width: ${({ width }) => width};

  position: relative;
  display: inline-block;
  ${({ variant, isLoading }) =>
    isLoading &&
    css`
      &::before {
        position: absolute;
        top: 50%;
        left: calc(50% - 2px);
        -webkit-transform: translate(-50%, -50%);
        transform: translate(-50%, -50%);
        content: '';
        display: block;
        width: 6px;
        height: 6px;
        border-radius: var(--border-radius-full);
        background-color: ${variant === BUTTON_TYPE.SECONDARY ? 'var(--text-secondary)' : 'var(--text-white)'};
        z-index: var(--z-index-base);
        margin-top: var(--space-4);
        -webkit-animation: 0.45s cubic-bezier(0, 0, 0.15, 1) infinite alternate ${ball};
        animation: 0.45s cubic-bezier(0, 0, 0.15, 1) infinite alternate ${ball};
        -webkit-animation-delay: 0.15s;
        animation-delay: 0.15s;
      }
      ${Buttons} {
        cursor: wait;
        color: transparent;
        &:hover {
          cursor: wait;
        }
        &::before {
          position: absolute;
          top: 50%;
          left: calc(50% - 2px);
          -webkit-transform: translate(-50%, -50%);
          transform: translate(-50%, -50%);
          content: '';
          display: block;
          width: 6px;
          height: 6px;
          border-radius: var(--border-radius-full);
          background-color: ${variant === BUTTON_TYPE.SECONDARY ? 'var(--text-secondary)' : 'var(--text-white)'};
          z-index: var(--z-index-base);
          margin-top: var(--space-4);
          -webkit-animation: 0.45s cubic-bezier(0, 0, 0.15, 1) infinite alternate ${ball};
          animation: 0.45s cubic-bezier(0, 0, 0.15, 1) infinite alternate ${ball};
          margin-left: -15px;
        }
        &::after {
          position: absolute;
          top: 50%;
          left: calc(50% - 2px);
          -webkit-transform: translate(-50%, -50%);
          transform: translate(-50%, -50%);
          content: '';
          display: block;
          width: 6px;
          height: 6px;
          border-radius: var(--border-radius-full);
          background-color: ${variant === BUTTON_TYPE.SECONDARY ? 'var(--text-secondary)' : 'var(--text-white)'};
          z-index: var(--z-index-base);
          margin-top: var(--space-4);
          -webkit-animation: 0.45s cubic-bezier(0, 0, 0.15, 1) infinite alternate ${ball};
          animation: 0.45s cubic-bezier(0, 0, 0.15, 1) infinite alternate ${ball};
          margin-left: var(--space-16);
          -webkit-animation-delay: 0.3s;
          animation-delay: 0.3s;
        }
      }
    `}
`;

const ball = keyframes`
  from {
    -webkit-transform: translateY(0) scaleY(0.8);
    transform: translateY(0) scaleY(0.8);
  }
  to {
    -webkit-transform: translateY(-10px);
    transform: translateY(-10px);
  }
`;

const Icon = styled.div<WrapButton>`
  ${FlexCenter}
  ${(props) =>
    props.disabled &&
    css`
      background: ${getBackground(props)};
      svg path {
        fill: var(--text-muted);
      }
      &:hover {
        background: ${getBackground(props)};
        /* background-color: var(--border-primary); */
      }
    `}

  ${({ variant }) =>
    variant === BUTTON_TYPE.SECONDARY &&
    css`
      svg path {
        fill: var(--text-primary);
      }
    `}
  ${({ size }) =>
    size === 'small' &&
    css`
      svg {
        width: 14px;
        height: 14px;
      }
    `}
  ${({ size }) =>
    size === 'large' &&
    css`
      svg {
        width: 20px;
        height: 20px;
      }
    `}
`;

export { Buttons, ButtonWrap, ball, Icon };
